import React, { useState, useEffect, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import { getOrganization, getClients, inviteClient, verifyClient } from '../services/businessService';
import {
  assignToClient, unassignFromClient, getClientSchedule,
  getClientProgress, getClientActivitySummary,
  getAvailablePrograms, getAvailableWorkouts, getClientNutritionPlans
} from '../services/businessFitnessService';
import { FaPlus, FaCog, FaDumbbell, FaUtensils, FaTrash, FaChevronDown, FaChevronUp, FaRunning, FaClipboardList } from 'react-icons/fa';
import { NutritionPlannerWizard } from './NutritionPlanner';
import './BusinessEmployeeDashboard.css';

export default function BusinessEmployeeDashboard() {
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const [org, setOrg] = useState(null);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clientSearch, setClientSearch] = useState('');
  const [clientStatusFilter, setClientStatusFilter] = useState('');

  const [showInviteForm, setShowInviteForm] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [inviting, setInviting] = useState(false);

  // Fitness/activity management
  const [expandedClient, setExpandedClient] = useState(null);
  const [assigningClient, setAssigningClient] = useState(null);
  const [managingClient, setManagingClient] = useState(null);
  const [clientSchedules, setClientSchedules] = useState({});
  const [clientProgressData, setClientProgressData] = useState({});
  const [clientActivitySummaries, setClientActivitySummaries] = useState({});
  const [clientNutritionData, setClientNutritionData] = useState({});
  const [availablePrograms, setAvailablePrograms] = useState([]);
  const [availableWorkouts, setAvailableWorkouts] = useState([]);
  const [assignType, setAssignType] = useState('program');
  const [assignContentId, setAssignContentId] = useState('');
  const [assignNotes, setAssignNotes] = useState('');
  const [nutritionPlannerMember, setNutritionPlannerMember] = useState(null);

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [orgData, clientsData] = await Promise.all([
        getOrganization(),
        getClients()
      ]);
      setOrg(orgData);
      setClients(clientsData.clients || clientsData || []);
    } catch (err) {
      toast.error(err.message || 'Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = useMemo(() => {
    let list = clients;
    if (clientSearch) {
      const q = clientSearch.toLowerCase();
      list = list.filter(c => (c.name || '').toLowerCase().includes(q) || (c.email || '').toLowerCase().includes(q));
    }
    if (clientStatusFilter) {
      list = list.filter(c => c.status === clientStatusFilter);
    }
    return list;
  }, [clients, clientSearch, clientStatusFilter]);

  const handleInviteClient = async (e) => {
    e.preventDefault();
    if (!inviteEmail.trim()) { toast.error('Email is required'); return; }
    setInviting(true);
    try {
      const res = await inviteClient({ email: inviteEmail.trim(), name: inviteName.trim() });
      if (res.emailSent) toast.success('Invite email sent to client');
      else toast.success('Client invited (email not configured)');
      setInviteEmail(''); setInviteName(''); setShowInviteForm(false);
      await loadData();
    } catch (err) {
      toast.error(err.message || 'Failed to invite client');
    } finally { setInviting(false); }
  };

  const handleVerifyClient = async (clientId) => {
    try {
      await verifyClient(clientId);
      toast.success('Client verified');
      setClients(prev => prev.map(c => c._id === clientId ? { ...c, status: 'active' } : c));
    } catch (err) {
      toast.error(err.message || 'Failed to verify client');
    }
  };

  // --- Fitness Handlers ---
  const loadActivitySummary = useCallback(async (memberId) => {
    try {
      const summary = await getClientActivitySummary(memberId);
      setClientActivitySummaries(prev => ({ ...prev, [memberId]: summary }));
    } catch { /* silent */ }
  }, []);

  useEffect(() => {
    clients.filter(c => c.status === 'active' && c.userId).forEach(c => loadActivitySummary(c._id));
  }, [clients, loadActivitySummary]);

  const loadContentLibrary = useCallback(async () => {
    try {
      const [progRes, workRes] = await Promise.all([getAvailablePrograms(), getAvailableWorkouts()]);
      setAvailablePrograms(progRes.programs || []);
      setAvailableWorkouts(workRes.workouts || []);
    } catch { /* silent */ }
  }, []);

  const handleToggleExpand = (clientId) => {
    setExpandedClient(prev => prev === clientId ? null : clientId);
    setAssigningClient(null);
    setManagingClient(null);
  };

  const handleOpenAssign = async (clientId) => {
    if (assigningClient === clientId) { setAssigningClient(null); return; }
    setAssigningClient(clientId); setManagingClient(null);
    setAssignType('program'); setAssignContentId(''); setAssignNotes('');
    if (availablePrograms.length === 0) await loadContentLibrary();
  };

  const handleAssignContent = async (memberId) => {
    if (!assignContentId) { toast.error('Select something to assign'); return; }
    try {
      await assignToClient(memberId, {
        type: assignType,
        ...(assignType === 'program' ? { programId: assignContentId } : { workoutId: assignContentId }),
        notes: assignNotes || ''
      });
      toast.success('Content assigned to client');
      setAssigningClient(null); setAssignContentId(''); setAssignNotes('');
      handleOpenManage(memberId);
      loadActivitySummary(memberId);
    } catch (err) { toast.error(err.message); }
  };

  const handleOpenManage = async (memberId) => {
    if (managingClient === memberId) { setManagingClient(null); return; }
    setManagingClient(memberId); setAssigningClient(null);
    try {
      const [schedule, progress, nutritionRes] = await Promise.all([
        getClientSchedule(memberId), getClientProgress(memberId), getClientNutritionPlans(memberId)
      ]);
      setClientSchedules(prev => ({ ...prev, [memberId]: schedule }));
      setClientProgressData(prev => ({ ...prev, [memberId]: progress }));
      setClientNutritionData(prev => ({ ...prev, [memberId]: nutritionRes.plans || [] }));
    } catch (err) { toast.error(err.message || 'Failed to load client details'); }
  };

  const handleUnassign = async (memberId, assignmentId) => {
    try {
      await unassignFromClient(memberId, assignmentId);
      toast.success('Assignment removed');
      const schedule = await getClientSchedule(memberId);
      setClientSchedules(prev => ({ ...prev, [memberId]: schedule }));
      loadActivitySummary(memberId);
    } catch (err) { toast.error(err.message); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—';
  const formatDuration = (mins) => {
    if (!mins) return '—';
    return mins < 60 ? `${Math.round(mins)}m` : `${Math.floor(mins / 60)}h ${Math.round(mins % 60)}m`;
  };

  if (loading) {
    return (
      <div className="employee-dashboard">
        <div className="employee-loading"><div className="loading-spinner" /><p>Loading dashboard...</p></div>
      </div>
    );
  }

  return (
    <div className="employee-dashboard">
      <div className="employee-dashboard-container">
        <header className="employee-dashboard-header">
          <div>
            <h1>{org?.name || 'My Organization'}</h1>
            <p>Employee Dashboard &mdash; {user.name || user.email}</p>
          </div>
        </header>

        <div className="employee-clients-header">
          <h2>My Clients</h2>
          <button className="business-btn business-btn-primary" onClick={() => setShowInviteForm(!showInviteForm)}>
            {showInviteForm ? 'Cancel' : 'Invite Client'}
          </button>
        </div>

        {showInviteForm && (
          <form className="business-invite-form" onSubmit={handleInviteClient}>
            <h3>Invite Client</h3>
            <div className="business-invite-fields">
              <input type="email" placeholder="Email *" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required />
              <input type="text" placeholder="Name" value={inviteName} onChange={(e) => setInviteName(e.target.value)} />
              <button type="submit" className="business-btn business-btn-primary" disabled={inviting}>
                {inviting ? 'Sending...' : 'Send Invite'}
              </button>
            </div>
          </form>
        )}

        <div className="business-clients-toolbar">
          <input type="text" className="business-search-input" placeholder="Search clients..."
            value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} />
          <select className="business-filter-select" value={clientStatusFilter}
            onChange={(e) => setClientStatusFilter(e.target.value)}>
            <option value="">All statuses</option>
            <option value="active">Active</option>
            <option value="pending">Pending</option>
            <option value="invited">Invited</option>
          </select>
        </div>

        <div className="business-client-list">
          {filteredClients.length === 0 ? (
            <div className="business-empty-state">
              <p>{clients.length === 0 ? 'No clients yet. Invite your first client to get started.' : 'No clients match your filters.'}</p>
            </div>
          ) : (
            filteredClients.map((client) => {
              const summary = clientActivitySummaries[client._id];
              const isActive = client.status === 'active';

              return (
                <div key={client._id} className={`business-client-card ${expandedClient === client._id ? 'expanded' : ''}`}>
                  <div className="business-client-row" onClick={() => isActive && handleToggleExpand(client._id)}>
                    <div className="business-client-info">
                      <strong>{client.name || client.email}</strong>
                      <span className="business-client-email">{client.email}</span>
                      {isActive && summary && (
                        <div className="business-client-activity-summary">
                          <span className="activity-chip"><FaDumbbell /> {summary.totalSessions} sessions</span>
                          {summary.sessionsThisWeek > 0 && <span className="activity-chip active"><FaRunning /> {summary.sessionsThisWeek} this week</span>}
                          {summary.assignedContentCount > 0 && <span className="activity-chip"><FaClipboardList /> {summary.assignedContentCount} assigned</span>}
                          {summary.activeNutritionPlan && <span className="activity-chip nutrition"><FaUtensils /> {summary.activeNutritionPlan.name}</span>}
                          {summary.lastWorkoutDate && <span className="activity-chip muted">Last: {formatDate(summary.lastWorkoutDate)}</span>}
                        </div>
                      )}
                    </div>
                    <div className="business-client-actions">
                      <span className={`business-status-badge status-${client.status}`}>{client.status}</span>
                      {client.status === 'pending' && (
                        <button className="business-btn business-btn-primary business-btn-small"
                          onClick={(e) => { e.stopPropagation(); handleVerifyClient(client._id); }}>Verify</button>
                      )}
                      {isActive && (
                        <>
                          <button title="Assign Content" className={`business-icon-btn ${assigningClient === client._id ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleOpenAssign(client._id); }}><FaPlus /></button>
                          <button title="Manage & Progress" className={`business-icon-btn ${managingClient === client._id ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); handleOpenManage(client._id); }}><FaCog /></button>
                          <button title="Nutrition" className={`business-icon-btn ${nutritionPlannerMember?.orgMemberId === client._id ? 'active' : ''}`}
                            onClick={(e) => { e.stopPropagation(); setNutritionPlannerMember(nutritionPlannerMember?.orgMemberId === client._id ? null : { orgMemberId: client._id, goalId: null }); }}><FaUtensils /></button>
                          <span className="business-expand-icon">{expandedClient === client._id ? <FaChevronUp /> : <FaChevronDown />}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Assign Panel */}
                  {isActive && assigningClient === client._id && (
                    <div className="business-assign-panel">
                      <h4>Assign Content</h4>
                      <div className="business-assign-type-toggle">
                        <button className={assignType === 'program' ? 'active' : ''} onClick={() => { setAssignType('program'); setAssignContentId(''); }}>Program</button>
                        <button className={assignType === 'workout' ? 'active' : ''} onClick={() => { setAssignType('workout'); setAssignContentId(''); }}>Workout</button>
                      </div>
                      <select className="business-assign-select" value={assignContentId} onChange={(e) => setAssignContentId(e.target.value)}>
                        <option value="">Select {assignType}...</option>
                        {(assignType === 'program' ? availablePrograms : availableWorkouts).map(item => (
                          <option key={item._id} value={item._id}>{item.name}</option>
                        ))}
                      </select>
                      <textarea className="business-assign-notes" placeholder="Notes for client (optional)"
                        value={assignNotes} onChange={(e) => setAssignNotes(e.target.value)} rows={2} />
                      <p className="business-assign-hint">This will appear in your client's "My Workouts" page.</p>
                      <div className="business-assign-actions">
                        <button className="business-btn business-btn-primary" onClick={() => handleAssignContent(client._id)}>Assign to Client</button>
                        <button className="business-btn business-btn-secondary" onClick={() => setAssigningClient(null)}>Cancel</button>
                      </div>
                    </div>
                  )}

                  {/* Manage & Progress Panel */}
                  {isActive && managingClient === client._id && (
                    <div className="business-manage-panel">
                      <div className="business-manage-section">
                        <h4><FaClipboardList /> Assigned Content</h4>
                        {(clientSchedules[client._id] || []).length === 0 ? (
                          <p className="business-empty-hint">No content assigned yet.</p>
                        ) : (
                          <div className="business-schedule-list">
                            {(clientSchedules[client._id] || []).map(item => (
                              <div key={item._id} className="business-schedule-item">
                                <span className={`schedule-type-icon ${item.type}`}>
                                  {item.type === 'program' ? <FaDumbbell /> : <FaRunning />}
                                </span>
                                <div className="schedule-item-info">
                                  <strong>{item.programId?.name || item.workoutId?.name || item.exerciseId?.name || 'Unknown'}</strong>
                                  <span className="schedule-item-meta">
                                    {item.type} · <span className={`schedule-status status-${item.status}`}>{item.status}</span>
                                    {item.notes && <> · {item.notes}</>}
                                  </span>
                                </div>
                                {item.status !== 'completed' && (
                                  <button className="business-icon-btn danger" title="Unassign" onClick={() => handleUnassign(client._id, item._id)}><FaTrash /></button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      <div className="business-manage-section">
                        <h4><FaRunning /> Recent Workout Sessions</h4>
                        {(() => {
                          const sessions = clientProgressData[client._id]?.sessions || [];
                          if (sessions.length === 0) return <p className="business-empty-hint">No workout sessions recorded yet.</p>;
                          return (
                            <div className="business-session-list">
                              {sessions.slice(0, 10).map(session => (
                                <div key={session._id} className="business-session-item">
                                  <div className="session-item-main">
                                    <strong>{session.workoutName || 'Workout'}</strong>
                                    <span className="session-item-date">{formatDate(session.startedAt || session.date)}</span>
                                  </div>
                                  <div className="session-item-stats">
                                    <span className={`session-status ${session.status}`}>{session.status}</span>
                                    {session.durationMinutes > 0 && <span>{formatDuration(session.durationMinutes)}</span>}
                                    {session.exercisesCompleted > 0 && <span>{session.exercisesCompleted} exercises</span>}
                                    {session.rating > 0 && <span>{'★'.repeat(session.rating)}</span>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      <div className="business-manage-section">
                        <h4><FaUtensils /> Nutrition Plans</h4>
                        {(() => {
                          const plans = clientNutritionData[client._id] || [];
                          if (plans.length === 0) return (
                            <div className="business-empty-hint">
                              <p>No nutrition plans yet.</p>
                              <button className="business-btn business-btn-small business-btn-primary"
                                onClick={() => setNutritionPlannerMember({ orgMemberId: client._id, goalId: null })}>Create Nutrition Plan</button>
                            </div>
                          );
                          return (
                            <div className="business-nutrition-list">
                              {plans.map(plan => (
                                <div key={plan._id} className="business-nutrition-item"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setNutritionPlannerMember({ orgMemberId: client._id, goalId: plan._id })}>
                                  <div className="nutrition-item-main">
                                    <strong>{plan.name}</strong>
                                    {plan.isActive && <span className="nutrition-active-badge">Active</span>}
                                  </div>
                                  <div className="nutrition-item-stats">
                                    <span>{plan.dailyCalories} cal</span>
                                    <span>{plan.dailyProtein}g protein</span>
                                    <span>{plan.dailyCarbs}g carbs</span>
                                    <span>{plan.dailyFat}g fat</span>
                                  </div>
                                  <span className="nutrition-item-date">Updated {formatDate(plan.updatedAt)}</span>
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Embedded Nutrition Planner */}
                      {nutritionPlannerMember?.orgMemberId === client._id && (
                        <div className="business-nutrition-embed">
                          <NutritionPlannerWizard
                            embedded
                            propOrgMemberId={nutritionPlannerMember.orgMemberId}
                            propGoalId={nutritionPlannerMember.goalId}
                            onClose={() => {
                              setNutritionPlannerMember(null);
                              loadActivitySummary(client._id);
                            }}
                          />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
